import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { ASK_USER_ANSWER_PRESENTATION_KIND, AUDIO_NOTE_PLANNER_KICKOFF_KIND } from '../../../types'
import type { AssistantMessage, MetadataEvent, ToolCall, UserMessage } from '../../../types'
import type { MessageGroup } from '../../../utils/messageGrouping'
import { useEditMessage, useRedoTurn } from '../../../hooks'
import { requestScrollFlagsRemeasure } from '../message-list/scroll-atoms'
import { isSynthContinuationId } from '../../../utils/synth-continuation'
import type { GroupContentPart } from './hooks/use-interleaved-parts'
import { resolveMessagePartRefs } from './tools/build-render-parts'
import { collectGroupToolCalls } from './tools/tool-call-utils'
import { areMessagePropsEqual } from './comparators'
import { useTurnActivity } from './hooks/use-turn-activity'
import { MessageHeader } from './nested/header'
import { Submessages } from './nested/submessages'
import { UserBubble, hasAttachment } from './nested/user-bubble'
import { AssistantMessageChrome } from './nested/assistant-chrome'
import { MessageArtifacts, StagedActionPreview } from './nested/artifacts'
import { MessageActionsRow } from './nested/actions-row'
import { MessageError } from './nested/message-error'
import { PlannerKickoffAttachments } from './nested/planner-kickoff-attachments'
import { messageStyles } from './nested/message-markdown'
import './turn-continuation.css'

// Backwards-compatible re-exports
export { MessageMarkdown, messageStyles } from './nested/message-markdown'

export interface MessageProps {
  group: MessageGroup
  isMostRecent?: boolean
  isLastUserGroup?: boolean
  onStagedActionClick?: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
  onReminderClick?: (reminderId: string) => void
}

type MessageInnerProps = MessageProps & {
  /** Whether this chat's turn is live (loading, streaming, or running tools). */
  isTurnActive: boolean
  streamingMetadata: MetadataEvent | undefined
  isExpanded: boolean
  setIsExpanded: (v: boolean) => void
}

// ---------------------------------------------------------------------------
// MessageRow — one message's chrome: bubble/body, artifacts, actions, errors
// ---------------------------------------------------------------------------

type MessageRowProps = {
  message: UserMessage | AssistantMessage
  isMostRecent?: boolean
  isLastUserGroup?: boolean
  group?: MessageGroup
  onStagedActionClick?: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
  onReminderClick?: (reminderId: string) => void
  groupToolCalls?: Array<ToolCall>
  groupContentParts?: Array<GroupContentPart>
  /** Whether this chat's turn is live (loading, streaming, or running tools). */
  isTurnActive: boolean
}

const MessageRow = memo(function MessageRow({
  message,
  isMostRecent,
  isLastUserGroup,
  group,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
  groupToolCalls,
  groupContentParts,
  isTurnActive,
}: MessageRowProps) {
  const renderedContentRef = useRef<HTMLDivElement>(null)
  const truncationRef = useRef<HTMLDivElement>(null)
  const [isEditMode, setIsEditMode] = useState(false)
  const [isMessageEditMode, setIsMessageEditMode] = useState(false)
  const [formattedContent, setFormattedContent] = useState('')
  const editedTextRef = useRef<string>('')

  const editMessage = useEditMessage()

  // Held in a ref because `useRedoTurn` rebuilds its callback every render (it
  // closes over the submit flow, which is rebuilt too). An unstable prop would
  // break the memo on the action row and re-render it per streaming token.
  const redoTurn = useRedoTurn()
  const redoTurnRef = useRef(redoTurn)
  useEffect(() => {
    redoTurnRef.current = redoTurn
  }, [redoTurn])
  const onRedoTurn = useCallback(
    () => redoTurnRef.current(message.conversation_id, message.id),
    [message.conversation_id, message.id],
  )

  // Narrow once via the role discriminant — everything below uses these
  // instead of re-asserting the union member with casts.
  const assistantMessage = message.role === 'assistant' ? message : undefined
  const userMessage = message.role === 'user' ? message : undefined
  const shouldAnimate = isMostRecent && isTurnActive

  const containsAttachment = useMemo(() => {
    return !!userMessage && hasAttachment(userMessage)
  }, [userMessage])

  // Entering edit mode snapshots the rendered HTML so the editor starts from
  // exactly what the user sees.
  useEffect(() => {
    if (isMessageEditMode && !isEditMode) {
      const sourceEl = message.role === 'user' ? truncationRef.current : renderedContentRef.current
      if (sourceEl) {
        setFormattedContent(sourceEl.innerHTML)
        setIsEditMode(true)
      }
    }
  }, [isMessageEditMode, isEditMode, message.role])

  const handleSaveEdit = useCallback(async () => {
    window.ampltd.trackEvent('Chat Message', { location: 'user message', action: 'Submit' })
    await editMessage(message.conversation_id, message.id, editedTextRef.current)
    setIsMessageEditMode(false)
    setIsEditMode(false)
  }, [setIsMessageEditMode, setIsEditMode, message.id, message.conversation_id])

  const handleCancelEdit = useCallback(() => {
    window.ampltd.trackEvent('Chat Message', { location: 'user message', action: 'Cancel' })
    setIsMessageEditMode(false)
    setIsEditMode(false)
  }, [setIsMessageEditMode, setIsEditMode])

  const onStartEditing = useCallback(() => {
    setIsMessageEditMode(true)
  }, [setIsMessageEditMode])

  if (message.role === 'user' && message.content === '') return null

  const presentation = message.role === 'user' ? message.metadata?.presentation : undefined
  if (presentation?.kind === ASK_USER_ANSWER_PRESENTATION_KIND && userMessage && presentation.label) {
    const presentationMessage = { ...userMessage, content: presentation.label }
    return (
      <div
        className={twMerge(
          messageStyles({ role: 'user', isSubmessage: false, hasNoAttachment: true }),
          'group/message relative pb-0',
        )}
        id={`message-${message.id}`}
        data-message-role={message.role}
        data-message-id={message.id}
      >
        <UserBubble
          message={presentationMessage}
          shouldAnimate={false}
          containsAttachment={false}
          isEditMode={false}
          formattedContent=""
          editedTextRef={editedTextRef}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={handleCancelEdit}
          contentRef={truncationRef}
        />
        <MessageActionsRow
          message={presentationMessage}
          isMostRecent={isMostRecent}
          containsAttachment={false}
          isTurnActive={isTurnActive}
          allowUserEditing={false}
          isEditing={false}
          onStartEditing={onStartEditing}
          onSaveEdit={handleSaveEdit}
          onCancelEdit={handleCancelEdit}
          onRedoTurn={onRedoTurn}
        />
      </div>
    )
  }
  if (presentation?.kind === AUDIO_NOTE_PLANNER_KICKOFF_KIND) {
    return (
      <div
        className="flex w-full justify-end px-4 py-4"
        id={`message-${message.id}`}
        data-message-role={message.role}
        data-message-id={message.id}
      >
        <PlannerKickoffAttachments
          content={typeof message.content === 'string' ? message.content : undefined}
          skill={presentation.skill}
        />
      </div>
    )
  }

  const actionsRow = (
    <MessageActionsRow
      message={message}
      isMostRecent={isMostRecent}
      containsAttachment={containsAttachment}
      isTurnActive={isTurnActive}
      allowUserEditing={!isSynthContinuationId(message.id)}
      isEditing={isMessageEditMode}
      onStartEditing={onStartEditing}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={handleCancelEdit}
      onRedoTurn={onRedoTurn}
    />
  )

  const artifacts = group && (
    <MessageArtifacts
      message={message}
      groupToolCalls={groupToolCalls}
      isMostRecent={isMostRecent}
      isTurnActive={isTurnActive}
      onStagedActionClick={onStagedActionClick}
      onStagedActionCreated={onStagedActionCreated}
      onReminderClick={onReminderClick}
    />
  )

  if (assistantMessage) {
    return (
      <AssistantMessageChrome
        message={assistantMessage}
        isSubmessage={false}
        isTurnActive={isTurnActive}
        isMostRecent={isMostRecent}
        editing={{
          isEditMode,
          formattedContent,
          editedTextRef,
          onSaveEdit: handleSaveEdit,
          onCancelEdit: handleCancelEdit,
        }}
        renderedContentRef={renderedContentRef}
        groupToolCalls={groupToolCalls}
        groupContentParts={groupContentParts}
      >
        {artifacts}
        {actionsRow}
      </AssistantMessageChrome>
    )
  }

  return (
    <>
      <div
        className={twMerge(
          messageStyles({ role: message.role, isSubmessage: false, hasNoAttachment: !containsAttachment }),
          'group/message relative pb-0',
        )}
        id={`message-${message.id}`}
        data-message-role={message.role}
        data-message-id={message.id}
      >
        {userMessage && (
          <UserBubble
            message={userMessage}
            shouldAnimate={shouldAnimate}
            containsAttachment={containsAttachment}
            isEditMode={isEditMode}
            formattedContent={formattedContent}
            editedTextRef={editedTextRef}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            contentRef={truncationRef}
          />
        )}

        {onStagedActionClick && userMessage && (
          <StagedActionPreview
            message={userMessage}
            isLastUserGroup={isLastUserGroup}
            isTurnActive={isTurnActive}
            onStagedActionClick={onStagedActionClick}
          />
        )}

        {artifacts}
        {actionsRow}
      </div>

      <MessageError message={message} />
    </>
  )
})

// ---------------------------------------------------------------------------
// Group container
// ---------------------------------------------------------------------------

const MessageInner = memo(function MessageInner({
  group,
  isMostRecent = false,
  isLastUserGroup = false,
  onStagedActionClick,
  onStagedActionCreated,
  onReminderClick,
  isTurnActive,
  streamingMetadata,
  isExpanded,
  setIsExpanded,
}: MessageInnerProps) {
  // Count only submessages that will render visible content
  const visibleSubmessageCount = useMemo(() => {
    if (!group.submessages) return 0
    return group.submessages.filter((msg) => {
      if (msg.role === 'tool' && typeof msg.content === 'string') {
        return msg.content.includes('favicon') || msg.name
      }
      // Non-tool submessages (assistant messages) — visible if they have content
      return msg.content && (typeof msg.content !== 'string' || msg.content.trim().length > 0)
    }).length
  }, [group.submessages])

  const onClickSearchResult = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handleHeaderToggle = useCallback(() => {
    const newExpanded = !isExpanded
    setIsExpanded(newExpanded)
    if (!newExpanded) {
      // Collapsing can pull the bottom back into view; re-measure once the
      // collapse spring (nested/submessages.tsx `expandTransition`) has
      // essentially settled.
      setTimeout(requestScrollFlagsRemeasure, 305)
    }
  }, [isExpanded, setIsExpanded])

  // Collect tool calls from assistant submessages so the primary message can show them
  const groupToolCalls = useMemo(() => {
    const toolCalls = collectGroupToolCalls(group, { includePrimary: false })
    return toolCalls.length > 0 ? toolCalls : undefined
  }, [group])

  // Collect interleaved content parts (text + tool_call refs) from all submessages in order
  const groupContentParts = useMemo(() => {
    if (!group.submessages) return undefined
    const parts: Array<GroupContentPart> = []
    group.submessages.forEach((msg, submessageIndex) => {
      if (msg.role !== 'assistant') return
      // Swap-stable identity for this message's part keys (parts/registry
      // contract); the positional fallback only covers identity-less messages,
      // the same documented exception `messageGroupKey` makes.
      const sourceId = msg.clientId ?? msg.id ?? `sub-${submessageIndex}`

      // Add thinking content before this message's other parts
      if (msg.thinkingContent?.trim()) {
        parts.push({
          type: 'thinking',
          text: msg.thinkingContent,
          sourceId,
          messageId: msg.id ?? undefined,
        })
      }

      for (const ref of resolveMessagePartRefs(msg)) {
        if (ref.type === 'text') {
          parts.push({ type: 'text', text: ref.text, sourceId, partIndex: ref.partIndex })
        } else {
          parts.push({ type: 'tool_call', toolCallId: ref.toolCallId })
        }
      }
    })
    return parts.length > 0 ? parts : undefined
  }, [group.submessages])

  const showMetadata = group.primaryMessage.role === 'assistant'

  // When the turn has tool calls, the tool card replaces the Thoughts header.
  // The primary's own calls count too: before the server rotates to the next
  // step they are the only ones, and the header would flash the running
  // tool's loader label above the card.
  const primaryHasToolCalls =
    group.primaryMessage.role === 'assistant' && !!(group.primaryMessage as AssistantMessage).toolCalls?.length
  const hasGroupTools = (!!groupToolCalls && groupToolCalls.length > 0) || primaryHasToolCalls

  // Settled groups (not most-recent, not the last user group) are the only
  // ones `settled-visibility.css` may skip offscreen — the excluded two are
  // what `use-last-turn-spacer` measures for the newest-turn anchor.
  const isSettled = !isMostRecent && !isLastUserGroup

  return (
    <div className="message-container" data-settled={isSettled ? '' : undefined}>
      {showMetadata && !hasGroupTools && visibleSubmessageCount > 0 && (
        <MessageHeader
          group={group}
          streamingMetadata={streamingMetadata}
          isMostRecent={isMostRecent}
          isTurnActive={isTurnActive}
          isExpanded={isExpanded}
          onToggle={handleHeaderToggle}
        />
      )}

      {/* Expanded submessages (hidden when tool card replaces them) */}
      {!hasGroupTools && (
        <Submessages
          isExpanded={isExpanded}
          submessages={group.submessages}
          onClickSearchResult={onClickSearchResult}
        />
      )}

      {/* Primary message content (tool card + text when hasGroupTools) */}
      <MessageRow
        message={group.primaryMessage}
        isMostRecent={isMostRecent}
        isLastUserGroup={isLastUserGroup}
        group={group}
        onStagedActionClick={onStagedActionClick}
        onStagedActionCreated={onStagedActionCreated}
        onReminderClick={onReminderClick}
        groupToolCalls={groupToolCalls}
        groupContentParts={groupContentParts}
        isTurnActive={isTurnActive}
      />
    </div>
  )
})

// ONE container type regardless of position. Activity-dependent behavior comes
// from `useTurnActivity`, which subscribes to the streaming atoms only when
// this group is live — so when a group stops being most recent, nothing about
// the element tree changes and expanded tool lists, in-progress reveals, and
// other component state survive the streaming→history seam by construction.
function MessageBase({ isMostRecent = false, isLastUserGroup = false, ...rest }: MessageProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // The most-recent group renders as the live turn even when it is also the
  // last user group (a just-sent message with no reply yet) — same precedence
  // the old per-position container types had.
  const effectiveIsLastUserGroup = isLastUserGroup && !isMostRecent
  const { isTurnActive, streamingMetadata } = useTurnActivity({
    isMostRecent,
    isLastUserGroup: effectiveIsLastUserGroup,
    conversationId: rest.group.primaryMessage.conversation_id,
  })

  return (
    <MessageInner
      {...rest}
      isMostRecent={isMostRecent}
      isLastUserGroup={effectiveIsLastUserGroup}
      isTurnActive={isTurnActive}
      streamingMetadata={streamingMetadata}
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
    />
  )
}

// No compound namespace: every sub-slot (header, submessages, row, …) is an
// internal of this container with no external consumer. Import the nested
// files directly if one ever needs a slot on its own.
export const Message = memo(MessageBase, areMessagePropsEqual)
Message.displayName = 'Message'
